/**
 * Materials.
 *
 * The road surface fills the bottom half of the frame at a 1.2 m eye height, so
 * it gets the whole texture budget and everything else gets a flat colour.
 *
 * Textures are generated procedurally into a canvas at startup rather than
 * downloaded. Three reasons, in order of importance:
 *
 *   1. Load time. The competitor takes 1-2 minutes to start; a sub-10-second
 *      start is a headline differentiator, and the cheapest way to keep it is
 *      to ship no texture bytes at all.
 *   2. Security. No remote texture fetch means the CSP can stay at
 *      `default-src 'none'` with no image host allowlisted.
 *   3. Anisotropy and mip quality are what stop markings shimmering as you
 *      drive - those are sampler settings, not source-resolution problems.
 *
 * When Stage E lands a real photographed asphalt scan, swap `makeAsphalt` for a
 * KTX2 load; nothing else here changes.
 */
import {
  CanvasTexture,
  Color,
  LinearMipmapLinearFilter,
  MeshBasicMaterial,
  MeshStandardMaterial,
  RepeatWrapping,
  DoubleSide,
  SRGBColorSpace,
  Texture,
  type WebGLRenderer,
} from 'three';
import { MARKINGS } from '../core/spec';
import { QUALITY_PROFILES, type Quality } from '../core/perf';

function canvas2d(size: number): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d', { willReadFrequently: false });
  if (!ctx) throw new Error('2D canvas unavailable - cannot generate materials');
  return { c, ctx };
}

/** Value noise, tileable. Cheap and good enough for aggregate. */
function noiseField(size: number, cells: number, seed: number): Float32Array {
  const g = new Float32Array(cells * cells);
  let s = seed >>> 0;
  const rnd = () => {
    // xorshift32 - deterministic, so the road looks the same every run.
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 100000) / 100000;
  };
  for (let i = 0; i < g.length; i++) g[i] = rnd();

  const out = new Float32Array(size * size);
  const scale = cells / size;
  const smooth = (t: number) => t * t * (3 - 2 * t);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const fx = x * scale;
      const fy = y * scale;
      const x0 = Math.floor(fx) % cells;
      const y0 = Math.floor(fy) % cells;
      const x1 = (x0 + 1) % cells;
      const y1 = (y0 + 1) % cells;
      const tx = smooth(fx - Math.floor(fx));
      const ty = smooth(fy - Math.floor(fy));
      const a = g[y0 * cells + x0]!;
      const b = g[y0 * cells + x1]!;
      const c = g[y1 * cells + x0]!;
      const d = g[y1 * cells + x1]!;
      out[y * size + x] = (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty;
    }
  }
  return out;
}

function configure(t: Texture, renderer: WebGLRenderer, repeat: number, quality: Quality): Texture {
  t.wrapS = RepeatWrapping;
  t.wrapT = RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.generateMipmaps = true;
  t.minFilter = LinearMipmapLinearFilter;
  // High anisotropy is the single setting that keeps a road surface crisp when
  // it is viewed at a grazing angle - which, from the driver's seat, is always.
  t.anisotropy = Math.min(
    QUALITY_PROFILES[quality].anisotropy,
    renderer.capabilities.getMaxAnisotropy(),
  );
  return t;
}

interface AsphaltTextures {
  map: Texture;
  normalMap: Texture;
  roughnessMap: Texture;
}

function makeAsphalt(renderer: WebGLRenderer, quality: Quality): AsphaltTextures {
  const size = QUALITY_PROFILES[quality].asphaltTextureSize;
  const fine = noiseField(size, Math.max(128, Math.round(size / 4)), 0x9e3779b9);
  const coarse = noiseField(size, Math.max(32, Math.round(size / 21)), 0x85ebca6b);

  // --- albedo ---
  const alb = canvas2d(size);
  const albData = alb.ctx.createImageData(size, size);
  for (let i = 0; i < size * size; i++) {
    // Hong Kong asphalt: a warm mid-grey. These are sRGB values, so 0.30-0.48
    // here is roughly 0.07-0.19 linear - the real albedo of worn bitumen. Set
    // it much lighter and the road reads as concrete.
    const v = 0.30 + fine[i]! * 0.13 + coarse[i]! * 0.05;
    const px = i * 4;
    albData.data[px] = Math.min(255, v * 255 * 1.02);
    albData.data[px + 1] = Math.min(255, v * 255);
    albData.data[px + 2] = Math.min(255, v * 255 * 0.97);
    albData.data[px + 3] = 255;
  }
  alb.ctx.putImageData(albData, 0, 0);

  // --- normal, from the height field the albedo was built out of ---
  const nrm = canvas2d(size);
  const nrmData = nrm.ctx.createImageData(size, size);
  const height = (x: number, y: number) => {
    const xi = ((x % size) + size) % size;
    const yi = ((y % size) + size) % size;
    const i = yi * size + xi;
    return fine[i]! * 0.75 + coarse[i]! * 0.25;
  };
  const STRENGTH = 3.2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (height(x + 1, y) - height(x - 1, y)) * STRENGTH;
      const dy = (height(x, y + 1) - height(x, y - 1)) * STRENGTH;
      const len = Math.hypot(dx, dy, 1);
      const px = (y * size + x) * 4;
      nrmData.data[px] = ((-dx / len) * 0.5 + 0.5) * 255;
      nrmData.data[px + 1] = ((-dy / len) * 0.5 + 0.5) * 255;
      nrmData.data[px + 2] = (1 / len) * 0.5 * 255 + 127;
      nrmData.data[px + 3] = 255;
    }
  }
  nrm.ctx.putImageData(nrmData, 0, 0);

  // --- roughness: polished wheel tracks are smoother than the rest ---
  const rgh = canvas2d(size);
  const rghData = rgh.ctx.createImageData(size, size);
  for (let i = 0; i < size * size; i++) {
    const x = i % size;
    const u = x / size;
    const track = Math.min(Math.abs(u - 0.45), Math.abs(u - 0.55));
    const trackPolish = Math.exp(-(track * track) / 0.00035) * 0.16;
    const r = 0.72 + fine[i]! * 0.2 - coarse[i]! * 0.08 - trackPolish;
    const px = i * 4;
    const v = Math.max(0, Math.min(255, r * 255));
    rghData.data[px] = v;
    rghData.data[px + 1] = v;
    rghData.data[px + 2] = v;
    rghData.data[px + 3] = 255;
  }
  rgh.ctx.putImageData(rghData, 0, 0);

  const map = new CanvasTexture(alb.c);
  map.colorSpace = SRGBColorSpace;
  const normalMap = new CanvasTexture(nrm.c);
  const roughnessMap = new CanvasTexture(rgh.c);

  return {
    map: configure(map, renderer, 1, quality),
    normalMap: configure(normalMap, renderer, 1, quality),
    roughnessMap: configure(roughnessMap, renderer, 1, quality),
  };
}

/** Alpha-tested 16 mm bars with <=110 mm clear gaps, with hand-built mips. */
function makeRailingAlpha(): Texture {
  const mipmaps: HTMLCanvasElement[] = [];
  let previous: HTMLCanvasElement | null = null;
  for (let size = 256; size >= 8; size = Math.floor(size / 2)) {
    const { c, ctx } = canvas2d(size);
    if (!previous) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#fff';
      // H2130H: 16 mm rounds, Q <=125 mm centre-to-centre, hence <=109 mm gap.
      const period = size * (0.125 / 1.5);
      const bar = size * (0.016 / 1.5);
      for (let x = 0; x < size + period; x += period) ctx.fillRect(x, 0, bar, size);
    } else {
      // Area averaging preserves the bars' coverage instead of letting each
      // tiny mip choose an arbitrary all-on/all-off binary sample.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(previous, 0, 0, size, size);
    }
    mipmaps.push(c);
    previous = c;
  }
  const texture = new CanvasTexture(mipmaps[0]!);
  texture.mipmaps = mipmaps;
  texture.generateMipmaps = false;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.wrapS = RepeatWrapping;
  return texture;
}

export interface Materials {
  readonly anisotropy: number;
  carriageway: MeshStandardMaterial;
  kerb: MeshStandardMaterial;
  marking: MeshStandardMaterial;
  yellowMarking: MeshStandardMaterial;
  carBody: MeshStandardMaterial;
  glass: MeshStandardMaterial;
  tyre: MeshBasicMaterial;
  contactShadow: MeshBasicMaterial;
  /** Street furniture: concrete (gullies, footbridge columns). */
  concrete: MeshStandardMaterial;
  /** Street furniture: galvanised/painted steel (lamp columns, brackets). */
  metal: MeshStandardMaterial;
  /** Pedestrian railing panels. */
  railing: MeshStandardMaterial;
  signalHead: MeshStandardMaterial;
  signalRed: MeshStandardMaterial;
  signalAmber: MeshStandardMaterial;
  signalGreen: MeshStandardMaterial;
  /** Building facade atlas: residential, shopfront and light-industrial bands. */
  facade: MeshStandardMaterial;
  facadeRoof: MeshStandardMaterial;
  setQuality(quality: Quality): void;
  dispose(): void;
}

export function createMaterials(renderer: WebGLRenderer, quality: Quality): Materials {
  const asphalt = makeAsphalt(renderer, quality);
  const railingAlpha = makeRailingAlpha();

  const carriageway = new MeshStandardMaterial({
    map: asphalt.map,
    normalMap: asphalt.normalMap,
    roughnessMap: asphalt.roughnessMap,
    roughness: 1,
    metalness: 0,
    color: 0xffffff,
  });
  carriageway.normalScale.set(0.85, 0.85);
  carriageway.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float roadAo;\nvarying float vRoadAo;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvRoadAo = roadAo;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vRoadAo;')
      .replace(
        'vec3 irradiance = getAmbientLightIrradiance( ambientLightColor );',
        'vec3 irradiance = getAmbientLightIrradiance( ambientLightColor ) * vRoadAo;',
      );
  };
  carriageway.customProgramCacheKey = () => 'road-sky-occlusion-v1';

  const kerb = new MeshStandardMaterial({
    color: 0x9a968d,
    roughness: 0.92,
    metalness: 0,
    vertexColors: true,
  });

  const marking = new MeshStandardMaterial({
    color: new Color(MARKINGS.COLOUR),
    roughness: 0.62,
    metalness: 0,
    // Coplanar with the carriageway. polygonOffset is what stops the z-fighting
    // that otherwise makes lines strobe as the camera moves - the visible
    // symptom the acceptance checklist calls out.
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -4,
  });
  const yellowMarking = marking.clone();
  yellowMarking.color.set(0xe2b91f);

  const carBody = new MeshStandardMaterial({ color: 0xd8dade, roughness: 0.35, metalness: 0.55 });
  const glass = new MeshStandardMaterial({
    color: 0x1b2430,
    roughness: 0.12,
    metalness: 0.2,
    transparent: true,
    opacity: 0.55,
  });
  const tyre = new MeshBasicMaterial({ color: 0x14161a });
  const contactShadow = new MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.24,
    depthWrite: false,
  });

  const concrete = new MeshStandardMaterial({ color: 0x8e8b83, roughness: 0.9 });
  const metal = new MeshStandardMaterial({ color: 0x59616b, roughness: 0.45, metalness: 0.7 });
  const railing = new MeshStandardMaterial({
    color: 0xb9bcc0,
    roughness: 0.5,
    metalness: 0.4,
    alphaMap: railingAlpha,
    alphaTest: 0.08,
    alphaToCoverage: true,
    transparent: false,
    side: DoubleSide,
  });
  const signalHead = new MeshStandardMaterial({ color: 0x171b18, roughness: 0.65 });
  const signalRed = new MeshStandardMaterial({ color: 0xff1717, emissive: 0x8a0505, emissiveIntensity: 1.5 });
  const signalAmber = new MeshStandardMaterial({ color: 0x442b09, roughness: 0.5 });
  const signalGreen = new MeshStandardMaterial({ color: 0x082f18, roughness: 0.5 });
  // The detailed facade atlas is created only by the optional buildings chunk.
  // Keep normal boots free of both its code and its startup canvas work.
  // P1a: facade is now atlas-driven for albedo + roughness. Base roughness
  // is 1 so the roughnessMap (concrete 0.7-0.85, glass 0.25-0.38) dominates
  // and metalness stays 0 — Hong Kong residential is not half-metal.
  const facade = new MeshStandardMaterial({ color: 0xffffff, roughness: 1.0, metalness: 0.0 });
  const facadeRoof = new MeshStandardMaterial({ color: 0x77736c, roughness: 0.94 });

  return {
    get anisotropy() {
      return asphalt.map.anisotropy;
    },
    carriageway,
    kerb,
    marking,
    yellowMarking,
    carBody,
    glass,
    tyre,
    contactShadow,
    concrete,
    metal,
    railing,
    signalHead,
    signalRed,
    signalAmber,
    signalGreen,
    facade,
    facadeRoof,
    setQuality(nextQuality) {
      const value = Math.min(
        QUALITY_PROFILES[nextQuality].anisotropy,
        renderer.capabilities.getMaxAnisotropy(),
      );
      for (const texture of [asphalt.map, asphalt.normalMap, asphalt.roughnessMap, facade.map, (facade as unknown as { roughnessMap?: Texture }).roughnessMap].filter(Boolean) as Texture[]) {
        texture.anisotropy = value;
        texture.needsUpdate = true;
      }
    },
    dispose() {
      for (const t of [asphalt.map, asphalt.normalMap, asphalt.roughnessMap, railingAlpha, facade.map, (facade as unknown as { roughnessMap?: Texture }).roughnessMap].filter(Boolean) as (Texture | undefined)[]) t?.dispose();
      for (const m of [carriageway, kerb, marking, yellowMarking, carBody, glass, tyre, contactShadow, concrete, metal, railing, signalHead, signalRed, signalAmber, signalGreen, facade, facadeRoof]) {
        m.dispose();
      }
    },
  };
}
